/**
 * GlobalDeepLinkHandler.jsx — sits at the root of the app.
 *
 * Responsibilities:
 *   1. Listen for Capacitor `App.addListener('appUrlOpen')` and route
 *      hustleapp://… URLs into the React Router via deepLinkRouter.
 *   2. Listen for any in-app wake-phrase match (fired from
 *      `wakeWord.js`) and route the same way.
 *   3. Handle the small set of side-effects (open scanner, open
 *      emergency screen) that need more than a navigation.
 */
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { navigateDeepLink } from "@/lib/deepLinkRouter";
import { toast } from "sonner";

export default function GlobalDeepLinkHandler() {
  const navigate = useNavigate();

  useEffect(() => {
    let capListener = null;

    // 1. Capacitor App URL open events (native iOS / Android)
    (async () => {
      try {
        const { App } = await import("@capacitor/app");
        capListener = await App.addListener("appUrlOpen", (data) => {
          if (!data?.url) return;
          handleDeepLink(data.url);
        });
      } catch {
        // Not running inside Capacitor — fine, we still handle wake events.
      }
    })();

    // 2. In-app wake-phrase events (from wakeWord.js)
    function onWakeEvent(ev) {
      const detail = ev?.detail;
      if (!detail?.deep_link) return;
      handleDeepLink(detail.deep_link);
    }
    window.addEventListener("hustle:wake", onWakeEvent);

    return () => {
      if (capListener) { try { capListener.remove(); } catch {} }
      window.removeEventListener("hustle:wake", onWakeEvent);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  function handleDeepLink(url) {
    const ok = navigateDeepLink({
      deepLink: url,
      navigate,
      onSideEffect: (kind) => {
        if (kind === "open-scanner") {
          // The scanner page itself opens the camera on mount.
          toast("Opening scanner…", { description: "Point your camera at the table QR code." });
        }
        if (kind === "emergency-screen") {
          toast("Emergency dialler open — tap a number to call.");
        }
      },
    });
    if (!ok) {
      console.warn("[deepLink] could not route", url);
    }
  }

  return null;
}
